import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup,
  CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiClient } from "@/lib/apiClient";
import { rotuloCodigoNome } from "@/lib/consultas";

export interface ProdutoResumo {
  id:          number;
  descricao:   string;
  codigoBarras: string | null;
  precoPadrao?: number;
}

interface Props {
  value?:      number;
  onChange:    (value: number) => void;
  /** Recebe o produto escolhido (p/ preencher descrição/preço na linha). */
  onSelectProduto?: (produto: ProdutoResumo) => void;
  placeholder?: string;
  disabled?:   boolean;
  className?:  string;
  /** Descrição já conhecida (linha em edição): mostra sem buscar o produto. */
  descricaoInicial?: string | null;
  /** Mostra o preço de venda na lista (DAV, OS, orçamento). */
  mostrarPreco?: boolean;
}

async function buscarProdutos(termo: string): Promise<ProdutoResumo[]> {
  const r = await apiClient.get<ProdutoResumo[]>("/api/produtos/buscar", {
    params: { termo, limite: 30 },
  });
  return r.data;
}

async function obterProduto(id: number): Promise<ProdutoResumo> {
  const r = await apiClient.get<ProdutoResumo>(`/api/produtos/${id}`);
  return r.data;
}

export function ProdutoSearchCombobox({
  value,
  onChange,
  onSelectProduto,
  placeholder = "Selecione um produto...",
  disabled,
  className,
  descricaoInicial,
  mostrarPreco,
}: Props) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");

  const { data: results = [], isLoading } = useQuery({
    queryKey: ["produto-search", search],
    queryFn:  () => buscarProdutos(search),
    enabled:  open && search.trim().length >= 2,
    staleTime: 10_000,
  });

  const { data: selected } = useQuery({
    queryKey: ["produto", value],
    queryFn:  () => obterProduto(value!),
    enabled:  !!value && !descricaoInicial,
    staleTime: 60_000,
  });
  const rotulo = selected
    ? rotuloCodigoNome(selected.id, selected.descricao)
    : value && descricaoInicial ? rotuloCodigoNome(value, descricaoInicial) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-normal", className)}
          disabled={disabled}
          type="button"
        >
          <span className="truncate">
            {rotulo ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[450px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Digite para buscar produto..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isLoading && (
              <CommandEmpty>
                {search.trim().length < 2
                  ? "Digite ao menos 2 caracteres..."
                  : "Nenhum produto encontrado."}
              </CommandEmpty>
            )}
            <CommandGroup>
              {results.map(p => (
                <CommandItem
                  key={p.id}
                  value={String(p.id)}
                  onSelect={() => { onChange(p.id); onSelectProduto?.(p); setOpen(false); }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === p.id ? "opacity-100" : "opacity-0")} />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{rotuloCodigoNome(p.id, p.descricao)}</span>
                    {(p.codigoBarras || (mostrarPreco && p.precoPadrao != null)) && (
                      <span className="text-xs text-muted-foreground">
                        {[p.codigoBarras,
                          mostrarPreco && p.precoPadrao != null
                            ? p.precoPadrao.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
                            : null,
                        ].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
